import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import {
  Box,
  Text,
  Textarea,
  ActionIcon,
  Button,
  Group,
  Skeleton,
  Stack,
  Loader,
  ThemeIcon,
} from '@mantine/core';
import SubscriptionBadge from 'views/Users/components/SubscriptionBadge';
import TherapyBadge from 'views/Users/components/TherapyBadge';
import { InlineIcon } from '@iconify/react';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';
import { getUserDisplayName } from 'views/Users/helpers/UsersViewUtils';
import { useGetUser } from 'views/Users/hooks/useGetUsers';
import { useMe } from 'store/userStore';
import { useAiCoachAssistantStore } from 'store/aiCoachAssistantStore';
import { MessageRole } from 'types';
import { useChatStyles } from './Chat.styles';
import { MessageContent } from './MessageContent';
import { QuickActions } from './QuickActions';
import { VoiceInput } from './VoiceInput';

export const Chat = () => {
  const { classes } = useChatStyles();
  const messagesRef = useRef<HTMLDivElement>(null);

  const me = useMe();
  const selectedUserId = useAiCoachAssistantStore((s) => s.selectedUserId);
  const conversation = useAiCoachAssistantStore((s) => s.conversation);
  const isSubmitInProgress = useAiCoachAssistantStore(
    (s) => s.isSubmitInProgress
  );
  const isHistoryLoading = useAiCoachAssistantStore((s) => s.isHistoryLoading);
  const toolStatuses = useAiCoachAssistantStore((s) => s.toolStatuses);
  const prefillText = useAiCoachAssistantStore((s) => s.prefillText);
  const setPrefillText = useAiCoachAssistantStore((s) => s.setPrefillText);
  const sendMessage = useAiCoachAssistantStore((s) => s.sendMessage);
  const setMessageRating = useAiCoachAssistantStore((s) => s.setMessageRating);
  const loadOlderMessages = useAiCoachAssistantStore(
    (s) => s.loadOlderMessages
  );
  const displayMode = useAiCoachAssistantStore((s) => s.displayMode);

  const [input, setInput] = useState('');
  const [isVoiceActive, setIsVoiceActive] = useState(false);

  const appendTranscript = (text: string) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text));
  };

  const { data: user } = useGetUser(selectedUserId || undefined);

  const activeInteraction = selectedUserId
    ? conversation[selectedUserId]
    : null;

  const allMessages =
    activeInteraction?.messages.filter((m) => m.role !== MessageRole.system) ??
    [];

  useEffect(() => {
    if (prefillText) {
      setInput(prefillText);
      setPrefillText('');
    }
  }, [prefillText, setPrefillText]);

  const scrollToBottom = () => {
    messagesRef.current?.scrollTo(0, messagesRef.current.scrollHeight);
  };

  // Scroll to bottom when switching users or when history finishes loading
  useEffect(() => {
    if (!isHistoryLoading && allMessages.length > 0) {
      isNearBottom.current = true;
      scrollToBottom();
    }
  }, [selectedUserId, isHistoryLoading]);

  const lastMessageContent = allMessages[allMessages.length - 1]?.content;
  const isNearBottom = useRef(true);

  const handleMessagesScroll = () => {
    const el = messagesRef.current;
    if (!el) return;
    isNearBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  useEffect(() => {
    if (isNearBottom.current) scrollToBottom();
  }, [allMessages.length, lastMessageContent, toolStatuses.length]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || !selectedUserId || !me || isSubmitInProgress) return;

    setInput('');
    await sendMessage({
      prompt: trimmed,
      userId: selectedUserId,
      coachId: me.userId,
      scrollToBottom,
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!user) {
    return (
      <Box className={classes.container} p="md">
        <Stack gap="sm">
          <Skeleton height={50} />
          <Skeleton height={200} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.userHeader}>
        <UserAvatar user={user} size={36} />
        <Box sx={{ flex: 1 }}>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              {getUserDisplayName(user)}
            </Text>
            <SubscriptionBadge
              status={user.subscriptionStatus}
              plan={user.subscriptionPlan}
              size="xs"
            />
            <TherapyBadge therapies={user.therapies} size="xs" />
          </Group>
          <Text size="xs" c="dimmed">
            {user.email}
          </Text>
        </Box>
      </Box>

      <Box className={classes.messagesWrap}>
        {/* The window can host a member's own chat too, so the assistant's
            never looks like one: this mark is only ever behind the AI. */}
        <Box className={classes.robotWatermark} aria-hidden>
          <InlineIcon icon="mdi:robot-happy" height={220} />
        </Box>

        <Box
          className={classes.messages}
          ref={messagesRef}
          onScroll={handleMessagesScroll}>
          {isHistoryLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                height={40}
                width="60%"
                sx={{ alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end' }}
              />
            ))}

          {selectedUserId &&
            !isHistoryLoading &&
            activeInteraction &&
            activeInteraction.messages.length <
              (activeInteraction.messageTotal ?? 0) && (
              <Button
                onClick={() => loadOlderMessages(selectedUserId)}
                variant="subtle"
                size="compact-xs"
                fullWidth>
                Load older messages...
              </Button>
            )}

          {!isHistoryLoading && allMessages.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Text size="sm" c="dimmed" ta="center">
                Start a conversation about {getUserDisplayName(user)}. Use quick
                actions below or type your own question.
              </Text>
            </Box>
          )}

          {allMessages.map((msg, i) => {
            if (msg.role === MessageRole.user) {
              return (
                <Box key={i} className={classes.userMessage}>
                  {msg.content}
                </Box>
              );
            }
            const rating = msg.rating ?? null;
            const canRate = selectedUserId != null && msg.id != null;
            return (
              <Box key={i} className={classes.assistantMessageWrap}>
                <Box className={classes.assistantMessage}>
                  <MessageContent content={msg.content} />
                </Box>
                {canRate && (
                  <Box
                    className={classes.ratingActions}
                    data-rating-actions=""
                    data-rated={rating != null ? 'true' : undefined}>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      aria-label="Like response"
                      onClick={() =>
                        setMessageRating({
                          userId: selectedUserId!,
                          messageId: msg.id!,
                          rating: rating === 1 ? null : 1,
                        })
                      }>
                      <InlineIcon
                        icon={
                          rating === 1 ? 'mdi:thumb-up' : 'mdi:thumb-up-outline'
                        }
                        width={14}
                      />
                    </ActionIcon>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      aria-label="Dislike response"
                      onClick={() =>
                        setMessageRating({
                          userId: selectedUserId!,
                          messageId: msg.id!,
                          rating: rating === -1 ? null : -1,
                        })
                      }>
                      <InlineIcon
                        icon={
                          rating === -1
                            ? 'mdi:thumb-down'
                            : 'mdi:thumb-down-outline'
                        }
                        width={14}
                      />
                    </ActionIcon>
                  </Box>
                )}
              </Box>
            );
          })}

          {isSubmitInProgress &&
            toolStatuses.length > 0 &&
            (() => {
              const statusByTool = new Map<string, (typeof toolStatuses)[0]>();
              for (const status of toolStatuses) {
                statusByTool.set(status.name, status);
              }
              return (
                <Box sx={{ marginBottom: 4 }}>
                  {Array.from(statusByTool.values()).map((status) => (
                    <Group
                      key={status.name}
                      gap={8}
                      wrap="nowrap"
                      sx={{ padding: '2px 0' }}>
                      {status.status === 'progress' ? (
                        <Loader size={14} />
                      ) : (
                        <ThemeIcon
                          size={18}
                          radius="xl"
                          color="teal"
                          variant="light">
                          <InlineIcon icon="mdi:check" width={12} />
                        </ThemeIcon>
                      )}
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {status.message}
                      </Text>
                    </Group>
                  ))}
                </Box>
              );
            })()}

          {isSubmitInProgress && toolStatuses.length === 0 && (
            <Box className={classes.typingDots}>
              <span />
              <span />
              <span />
            </Box>
          )}
        </Box>
      </Box>

      <QuickActions compact={displayMode === 'widget'} />

      <Box className={classes.inputArea}>
        <Textarea
          placeholder="Ask about this user..."
          autosize
          maxRows={4}
          sx={{ flex: 1, display: isVoiceActive ? 'none' : undefined }}
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          disabled={isSubmitInProgress}
        />
        <VoiceInput
          disabled={isSubmitInProgress}
          onTranscript={appendTranscript}
          onStateChange={setIsVoiceActive}
        />
        {!isVoiceActive && (
          <ActionIcon
            size="lg"
            color="teal"
            variant="filled"
            onClick={handleSend}
            disabled={!input.trim() || isSubmitInProgress}
            loading={isSubmitInProgress}>
            <InlineIcon icon="fluent:send-28-regular" height={20} />
          </ActionIcon>
        )}
      </Box>
    </Box>
  );
};
